import footerMessagesDe from "@userbrain/website-ui/locales/de/footer.json";
import footerMessagesEn from "@userbrain/website-ui/locales/en/footer.json";
import headerMessagesDe from "@userbrain/website-ui/locales/de/header.json";
import headerMessagesEn from "@userbrain/website-ui/locales/en/header.json";
import React, { useEffect, useMemo } from "react";
import { IntlProvider, Link, navigate, useIntl } from "gatsby-plugin-intl";
import PropTypes from "prop-types";
import { cn } from "@sys42/utils";
import CookieDisclaimer, {
  initCookieDisclaimer,
} from "@userbrain/cookie-disclaimer";
import { Footer, Header, WebsiteContext } from "@userbrain/website-ui";
import { NotTranslatedInfoBox } from "../components/NotTranslatedInfoBox";
import { Banner } from "../components/Banner";
import * as styles from "./layout.module.css";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { setTrafficSource, cookieDomain, setRefcode } from "../helpers";
import { Script } from "gatsby";

initCookieDisclaimer(cookieDomain);

const Layout = (props) => {
  const { children, activeMenuItem, isTranslated, customHeader } = props;

  const intl = useIntl();

  useEffect(() => {
    const url = new URL(window.location.href);

    const refcode = url.searchParams.get("refcode");
    if (refcode) {
      setRefcode(refcode);
    }

    if (url.searchParams.get("utm_source")) {
      let trafficSource = url.searchParams.get("utm_source");
      const adgroupid = url.searchParams.get("adgroupid");
      const campaignid = url.searchParams.get("campaignid");
      if (adgroupid) {
        trafficSource = `${trafficSource}:adgroupid=${adgroupid}`;
      } else if (campaignid) {
        trafficSource = `${trafficSource}:campaignid=${campaignid}`;
      }
      setTrafficSource(trafficSource);
    } else if (url.searchParams.get("gclid")) {
      setTrafficSource("adwords:NOTRACKING");
    } else if (url.searchParams.get("ref")) {
      const refValue = url.searchParams.get("ref");
      setTrafficSource(`ref:${refValue}`);
    }
  }, []);

  function enableTracking() {
    if (window) {
      // Activate tracking scripts
      if (typeof window.enableTracking === "function") {
        window.enableTracking();
      }
      const url = new URL(window.location.href);
      // add more specific tracking source
      if (
        !url.searchParams.get("utm_source") &&
        url.searchParams.get("gclid")
      ) {
        setTrafficSource("adwords:" + url.searchParams.get("gclid"));
      }
    }
  }

  return (
    <WebsiteContext.Provider
      value={{
        websiteType: "website",
        Link,
        navigate,
        formatMessage: intl.formatMessage,
      }}
    >
      <Banner bannerId={"webinar-july-2025"} dayEnd={"2025-07-23"}>
        📹 Join our live webinar on July 23 @5pm CET: "Real User Testing for the
        Age of AI"{" "}
        <a href="https://app.livestorm.co/userbrain/real-user-testing-for-the-age-of-ai?utm_source=banner&utm_medium=website&utm_campaign=webinar-july-2025">
          Register now
        </a>
      </Banner>
      {/*<Banner bannerId={'ub2021'}>Woah — everything looks different! No worries… we’re still the same, but
                better. <a href={'https://www.userbrain.com/blog/userbrain-revamp/'}>Learn more</a></Banner>*/}
      {/*<Banner bannerId={'prices102022'} dayEnd={'2022-10-10'}>📢 We’ll increase the prices for all Userbrain plans on October 11th. Save up to 20% by subscribing today. <AutoLink href={'https://www.userbrain.com/blog/updates-to-userbrains-pricing/'}>Learn more</AutoLink></Banner>*/}
      {/*{<Banner bannerId={'job082022'}>🧳 We’re hiring a Digital Marketing Specialist. <AutoLink to={'/careers'}>Learn more</AutoLink></Banner>}*/}

      {/* <Banner bannerId={`webinar2023-03-28`} dayEnd={'2023-03-28'}>📹 Live Webinar: UX vs. AI - The Need for Excellent UX in the Age of AI <AutoLink href="https://us02web.zoom.us/webinar/register/2116793971692/WN_9mjQYYwBR5K5iyvXO3teEg">
              Learn more
            </AutoLink>
            </Banner> */}
      {/*<Banner bannerId={`hiring2023-06`}>💼 We are hiring. Check out our open positions <AutoLink to={'/careers'}>
              Learn more
            </AutoLink>
            </Banner> */}

      <div
        style={{
          position: "relative",
          paddingTop: "var(--height-header)",
        }}
      >
        {customHeader || <Header activeMenuItem={activeMenuItem} />}
        {!isTranslated && intl.locale !== "en" ? (
          <>
            <main id="main" role="main" lang="en">
              {children}
            </main>
            <NotTranslatedInfoBox />
          </>
        ) : (
          <>
            <main id="main" role="main">
              {children}
            </main>
          </>
        )}
        <Footer
          style={{ marginTop: "var(--spacing-lg)" }}
          languageSwitcher={<LanguageSwitcher />}
        />

        <CookieDisclaimer
          className={styles.cookieDisclaimer}
          onConsentGiven={enableTracking}
          classNameButtonAgree={cn(styles.cookieDisclaimerButtonAgree)}
          classNameButtonDecline={styles.cookieDisclaimerButtonDecline}
        />

        <Script id="gtm-script">
          {`window.dataLayer = window.dataLayer || []; dataLayer.push({'event':'pageview', 'gitBranch':'${
            process.env.BRANCH ? process.env.BRANCH : "master"
          }'}); window.enableTracking = function(){(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','GTM-NB7SLF4'); window.enableTracking = function(){}};if(document.cookie.indexOf("ncc=true") >= 0 || document.cookie.indexOf("CookieConsent=true") >= 0 || document.cookie.indexOf("cookie_consent=true") >= 0){ window.enableTracking() }`}
        </Script>
      </div>
    </WebsiteContext.Provider>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

const LayoutWithAllTranslations = (props) => {
  const intl = useIntl();
  const lang = intl.locale.substring(0, 2);

  const messages = useMemo(() => {
    // XXX: All messages for all languages are loaded here. This could be optimized by using different layouts for different languages.
    const messagesHeader = lang === "de" ? headerMessagesDe : headerMessagesEn;
    const messagesFooter = lang === "de" ? footerMessagesDe : footerMessagesEn;
    return {
      ...intl.messages,
      ...messagesHeader,
      ...messagesFooter,
    };
  }, [intl.messages, lang]);

  return (
    <IntlProvider locale={intl.locale} messages={messages}>
      <Layout {...props} />
    </IntlProvider>
  );
};

export default LayoutWithAllTranslations;
